/**
 * ApiKeysView — BYOAPI provider key management.
 *
 * S-068 / FEAT-006 (Phase 7). Replaces the AdminStubs.ApiKeysView
 * placeholder. Admin-only screen reachable from Settings → API keys.
 *
 * What it does:
 *   - Workspace selector (top) — calls useActiveWorkspace().activate()
 *     on change so cross-workspace mutations land under the right RLS
 *     scope. Mirror PublishTargetsView's pending-select pattern so the
 *     selector reflects intent during activate() without flicker.
 *   - Two scoped sections:
 *       * Tenant-level keys (agencyclient_id IS NULL) — available to
 *         every workspace as the fallback in the BYOAPI resolution
 *         hierarchy (FEAT-005 BYOAPIResolver).
 *       * Workspace-level keys (agencyclient_id = activeWsId) — only
 *         the active workspace; override the tenant default for that
 *         workspace's pipeline runs.
 *   - Each section is its own GET /api-keys?agencyclient_id=<scope>
 *     fetch. "null" filter for tenant scope, numeric id for workspace.
 *   - Table columns: Provider, Key (masked echo), Status, Last tested,
 *     Created, Actions (Test / Delete).
 *
 * Add key modal:
 *   - Scope dropdown (workspace default — more common case)
 *   - Provider dropdown — anthropic | openai | pexels | unsplash | jina
 *     (mirrors PROVIDERS in apps/api/src/routes/api-keys.ts; pexels/
 *     unsplash exist server-side even though the plan focuses on the
 *     Anthropic/OpenAI/Gemini/Jina set — including all five keeps the
 *     SPA aligned with the API enum and avoids 422s if the operator
 *     ever needs to land a stock-photo key).
 *   - Key input is <input type="password"> with autoComplete="new-password",
 *     spellCheck off. The state for the key lives only inside the modal
 *     component and is cleared on close/success. It is never put in a
 *     log line, toast, or any persistent store.
 *   - On success the response payload includes `key_last4` and a
 *     decrypted ciphertext (admin path of maskCiphertext) — we display
 *     only the masked echo `<prefix>***<key_last4>` derived from the
 *     plaintext server response, never the full plaintext. ADR-002.
 *   - On 409 (active_key_exists) we surface inline error "An active key
 *     for this provider already exists at this scope."
 *
 * Test button:
 *   - POST /api-keys/:id/test → real network call to the provider.
 *     Shows a spinner during the call, then updates the row in place
 *     with ✓/✗ + relative timestamp. We invalidate ["api-keys"] so
 *     the row's last_tested_at + last_tested_status reflect the new
 *     state from the server (single source of truth).
 *
 * Delete confirm:
 *   - "Remove this key? Pipeline runs will fall back to the next level
 *     in the resolution hierarchy." per the plan. Destructive-styled
 *     confirm button.
 *
 * Security (ADR-002 + FEAT-006 § API key entry safety):
 *   - Never log the plaintext key — no console.log, no toast.
 *   - Never echo the plaintext key back to the user. The server's
 *     `key_ciphertext` admin-decryption is intentionally NOT rendered;
 *     we render `<prefix>***<key_last4>` from `key_last4` only.
 *   - Add modal: password input, autoComplete new-password,
 *     spellCheck=false. Key state is component-local and dropped on
 *     unmount.
 *   - No edit drawer in v1 — the v1 mental model is "create new + delete
 *     old to rotate". Rotation through PATCH /api-keys/:id with a fresh
 *     `key` is supported server-side but would require a second password
 *     input pass and is deferred to a follow-up if operators ask for it.
 *
 * RBAC:
 *   - Route reachable only via the admin-gated Settings rail (LeftRail
 *     hides it for non-admins).
 *   - The API enforces requireAdmin; non-admins would see 403 on every
 *     mutation, surfaced inline via ApiError.
 */

import { useEffect, useMemo, useState } from "react";
// Consolidation (option 3): shared admin modal shell. Replaces the local
// ModalShell that used to live at the bottom of this file. (This view has no
// drawer, so it imports ModalShell only.)
import { ModalShell } from "@/components/SettingsModalShell";
import { SectionHeader } from "@/components/SectionHeader";
import { useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL, ApiError, useApi, useApiMutation, useApiQuery } from "@/lib/api";
import { buildCreateKeyBody, providerTakesBaseUrl } from "@/lib/api-key-body";
import { useActiveWorkspace, useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import {
  keyConsequences,
  type PublicationLike,
} from "@/lib/key-consequences";

// Mirrors apps/api/src/routes/api-keys.ts CREATABLE_PROVIDERS.
//
// Founder decision, 2026-09-25 — "zernio" is deliberately absent. It's
// provisioned exclusively by provision-workspace.ts's own direct DB
// insert, never through this route (confirmed by grep). This dropdown
// option was leftover scaffolding for a BYOAPI-Zernio flow that was
// never built — until it is, a human creating one here would produce a
// row that GET /api-keys now hides forever and PATCH/DELETE now refuse
// to touch, an orphaned dead end.
const PROVIDERS = [
  // LLM providers
  "anthropic",
  "openai",
  "gemini",
  "xai",
  "zhipu",
  "qwen",
  "deepseek",
  "mistral",
  // Image/search providers
  "pexels",
  "unsplash",
  "jina",
  // Social dispatch providers (FEAT-097)
  "buffer",
] as const;
type Provider = (typeof PROVIDERS)[number];

const PROVIDER_LABEL: Record<Provider, string> = {
  anthropic: "Anthropic (Claude)",
  openai: "OpenAI (GPT)",
  gemini: "Google Gemini",
  xai: "xAI (Grok)",
  zhipu: "Zhipu AI (GLM)",
  qwen: "Alibaba (Qwen)",
  deepseek: "DeepSeek",
  mistral: "Mistral",
  pexels: "Pexels",
  unsplash: "Unsplash",
  jina: "Jina",
  buffer: "Buffer (social publishing)",
};

// Mask prefix per provider. We render `<prefix>***<last4>` without
// ever reading the plaintext server payload — this is purely a
// display convention. Real keys carry these prefixes; the placeholder
// "key-" covers anything we don't recognize.
const PROVIDER_PREFIX: Record<Provider, string> = {
  anthropic: "sk-ant-",
  openai: "sk-",
  gemini: "AIza",
  xai: "xai-",
  zhipu: "key-",
  qwen: "key-",
  deepseek: "sk-",
  mistral: "key-",
  pexels: "key-",
  unsplash: "key-",
  jina: "jina_",
  buffer: "key-",
};

// FEAT-071 / S-339 — providers with an inbound webhook to configure.
// Empty today: zernio was the only member, and GET /api-keys no longer
// ever returns a zernio row (2026-09-25) — kept as a set, not deleted
// outright, so the next webhook-using provider is a one-line addition
// rather than reintroducing the pattern from scratch.
const WEBHOOK_PROVIDERS = new Set<Provider>([]);

type Scope = "tenant" | "workspace";

const SCOPE_LABEL: Record<Scope, string> = {
  tenant: "Tenant",
  workspace: "Workspace",
};

interface Agencyclient {
  id: number;
  name: string;
  slug: string;
}

// Server row shape from apps/api/src/routes/api-keys.ts serializeRow().
interface ApiKeyRow {
  id: string; // bigint serialized as string
  tenant_id: number;
  agencyclient_id: number | null;
  provider: string;
  key_last4: string;
  // We intentionally do not read this field for display. Admin requests
  // receive the decrypted plaintext per ADR-002, but we never render it
  // anywhere — only `key_last4` is shown.
  key_ciphertext: string | null;
  status: string; // active | error | rotating | revoked
  last_tested_at: string | null;
  last_tested_status: string | null;
  last_used_at: string | null;
  created_at: string;
  // FEAT-071 / S-339 — routing key for the per-tenant webhook URL, not a
  // secret itself. null for providers that don't use inbound webhooks.
  webhook_token: string | null;
  // BUG-124 — the key's own API address (a Qwen workspace's); null uses the
  // provider's default endpoint.
  base_url?: string | null;
}

interface ApiKeysResponse {
  data: ApiKeyRow[];
  meta: { total: number; limit: number; offset: number };
}

const AK_WS_KEY = "ccm:api-keys:active_ws";

// ---------- Helpers ----------

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

// Relative time without pulling in date-fns. "5m ago" / "3h ago" /
// "2d ago" / "Jan 5". Good enough for last-tested-at; falls back to
// absolute date when the diff exceeds a week.
function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return "Never";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMs = now - then;
    if (diffMs < 60_000) return "Just now";
    const mins = Math.round(diffMs / 60_000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days}d ago`;
    return fmtDate(iso);
  } catch {
    return "—";
  }
}

function isKnownProvider(value: string): value is Provider {
  return (PROVIDERS as readonly string[]).includes(value);
}

// `<prefix>***<last4>` built without touching the plaintext server
// payload. Pure presentation; safe to render for both admins and
// (theoretically) non-admins.
function maskedEcho(provider: string, last4: string | null | undefined): string {
  const prefix = isKnownProvider(provider) ? PROVIDER_PREFIX[provider] : "";
  const tail = last4 && last4.length > 0 ? last4 : "????";
  return `${prefix}***${tail}`;
}

function providerLabel(provider: string): string {
  return isKnownProvider(provider) ? PROVIDER_LABEL[provider] : provider;
}

// ---------- Top-level view ----------

export function ApiKeysView() {
  const { data: workspaces, isLoading: wsLoading, error: wsError } =
    useApiQuery<Agencyclient[]>(["agencyclients"], "/agencyclients");

  const [selectedWsId, setSelectedWsId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(AK_WS_KEY);
    return raw ? Number(raw) : null;
  });

  useEffect(() => {
    if (selectedWsId != null) {
      try {
        window.sessionStorage.setItem(AK_WS_KEY, String(selectedWsId));
      } catch {
        /* sessionStorage unavailable — tolerate */
      }
    }
  }, [selectedWsId]);

  // Default to first workspace once the list loads.
  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      const stillValid = workspaces.some((w) => w.id === selectedWsId);
      if (!stillValid) setSelectedWsId(workspaces[0].id);
    }
  }, [workspaces, selectedWsId]);

  if (wsLoading) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <div className="state-loading">Loading API keys…</div>
        </div>
      </main>
    );
  }

  if (wsError) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <div className="state-error">
            {wsError instanceof ApiError && wsError.status === 403
              ? "You don't have permission to view API keys."
              : (wsError.message ?? "Could not load workspaces.")}
          </div>
        </div>
      </main>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <Header />
          <div className="settings-empty">
            <h2>No workspaces yet</h2>
            <p>
              Create a workspace under Settings → Workspaces before adding
              workspace-scoped API keys. Tenant-level keys still apply.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <ApiKeysBody
      workspaces={workspaces}
      selectedWsId={selectedWsId}
      onSelectWs={setSelectedWsId}
    />
  );
}

function Header() {
  return (
    <>
      <nav className="editor-breadcrumb" aria-label="Breadcrumb">
        <span>Settings</span>
        <span className="editor-breadcrumb-sep">/</span>
        <span>API keys</span>
      </nav>
      <SectionHeader
        eyebrow="Settings · API Keys"
        title="API keys"
        description="BYOAPI provider keys. Stored encrypted at rest with AES-256-GCM and never echoed back to the browser as plaintext after save. Tenant-level keys apply to every workspace; workspace-level keys override them per agencyclient."
      />
    </>
  );
}

interface BodyProps {
  workspaces: Agencyclient[];
  selectedWsId: number | null;
  onSelectWs: (id: number) => void;
}

function ApiKeysBody({ workspaces, selectedWsId, onSelectWs }: BodyProps) {
  const qc = useQueryClient();
  const activeWs = useMemo(
    () => workspaces.find((w) => w.id === selectedWsId) ?? workspaces[0],
    [workspaces, selectedWsId]
  );
  const activeWsId = activeWs.id;

  // S-199 — sync the JWT agencyclient_id claim to the displayed workspace on
  // load so reads (RLS scope) and mutations target it.
  useEnsureActiveWorkspace(activeWsId);

  const { activate, isActivating, error: switchError, clearError } =
    useActiveWorkspace();

  // Tenant-scoped keys: GET /api-keys?agencyclient_id=null. The route
  // checks the literal string "null" (not the absence of the param) to
  // distinguish "tenant-level keys only" from "everything".
  const {
    data: tenantResp,
    isLoading: tenantLoading,
    error: tenantError,
  } = useApiQuery<ApiKeysResponse>(
    ["api-keys", "tenant"],
    "/api-keys?agencyclient_id=null"
  );

  const {
    data: wsResp,
    isLoading: wsKeysLoading,
    error: wsKeysError,
  } = useApiQuery<ApiKeysResponse>(
    ["api-keys", "workspace", activeWsId],
    `/api-keys?agencyclient_id=${activeWsId}`
  );

  const tenantKeys = tenantResp?.data ?? [];
  const workspaceKeys = wsResp?.data ?? [];

  // S-376 — a failing key is a fact about the key; what it costs is a fact
  // about the work. These two queries turn "the OpenAI key is in error" into
  // "The Long Read routes to OpenAI, so it is silently falling back."
  const { data: pubsResp } = useApiQuery<{ data: PublicationLike[] }>(
    ["publications", activeWsId],
    `/publications?agencyclient_id=${activeWsId}`
  );
  const consequences = keyConsequences(
    [...workspaceKeys, ...tenantKeys],
    pubsResp?.data ?? [],
    (activeWs as { preferredProvider?: string | null; preferred_provider?: string | null })
      .preferredProvider ??
      (activeWs as { preferred_provider?: string | null }).preferred_provider ??
      null
  );

  const [showCreate, setShowCreate] = useState(false);
  const [createScope, setCreateScope] = useState<Scope>("workspace");
  const [confirmDelete, setConfirmDelete] = useState<ApiKeyRow | null>(null);
  // Phase 5 UX polish parity: pending-select pattern so the workspace
  // selector reflects intent during activate() without flicker.
  const [pendingWsId, setPendingWsId] = useState<number | null>(null);
  // Inline "Saved" pill on the newly-created key. We only know which
  // row is "newly created" by id, so we keep that id around for a few
  // seconds after the create modal closes.
  const [recentlySavedId, setRecentlySavedId] = useState<string | null>(null);

  useEffect(() => {
    if (!recentlySavedId) return;
    const t = window.setTimeout(() => setRecentlySavedId(null), 2500);
    return () => window.clearTimeout(t);
  }, [recentlySavedId]);

  function invalidateBoth() {
    void qc.invalidateQueries({ queryKey: ["api-keys", "tenant"] });
    void qc.invalidateQueries({
      queryKey: ["api-keys", "workspace", activeWsId],
    });
  }

  async function handleWorkspaceChange(nextId: number) {
    if (nextId === activeWsId) return;
    clearError();
    setPendingWsId(nextId);
    try {
      await activate(nextId);
      onSelectWs(nextId);
    } catch {
      /* useActiveWorkspace surfaces error via switchError */
    } finally {
      setPendingWsId(null);
    }
  }

  function openCreate(scope: Scope) {
    setCreateScope(scope);
    setShowCreate(true);
  }

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <Header />

        <div className="team-toolbar">
          <span className="team-toolbar-label">Workspace</span>
          <select
            className="settings-select"
            value={pendingWsId ?? activeWsId}
            disabled={isActivating}
            onChange={(e) => {
              void handleWorkspaceChange(Number(e.target.value));
            }}
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.slug})
              </option>
            ))}
          </select>
          {isActivating && (
            <span className="team-toolbar-hint">Switching workspace…</span>
          )}
          {switchError && !isActivating && (
            <span className="settings-field-error">
              {switchError.status === 403
                ? "You don't have access to that workspace."
                : (switchError.message ?? "Could not switch workspace.")}
            </span>
          )}
          <button
            type="button"
            className="btn-primary"
            onClick={() => openCreate("workspace")}
            disabled={isActivating}
            style={{ marginLeft: "auto" }}
          >
            + Add key
          </button>
        </div>

        {consequences.length > 0 && (
          <div className="settings-warn-block">
            <strong>
              {consequences.length === 1
                ? "A provider key is not working"
                : `${consequences.length} provider keys are not working`}
            </strong>
            {consequences.map((c) => (
              <p key={c.provider}>
                <strong>{c.provider}</strong> has no working key at either
                scope.{" "}
                {c.publications.length === 0 ? (
                  <>
                    Nothing routes to it today, so no piece is affected — but
                    any publication set to {c.provider} would silently fall
                    back to another provider.
                  </>
                ) : (
                  <>
                    {c.publications.join(", ")}{" "}
                    {c.publications.length === 1 ? "is" : "are"} set to{" "}
                    {c.provider}, so {c.publications.length === 1 ? "it" : "they"}{" "}
                    {c.publications.length === 1 ? "is" : "are"} silently
                    falling back — the pieces still generate, on a model you
                    did not choose.
                  </>
                )}
              </p>
            ))}
          </div>
        )}

        <ApiKeysSection
          title="Workspace-level keys"
          subtitle={`Keys scoped to ${activeWs.name}. Override tenant keys for pipeline runs under this workspace.`}
          loading={wsKeysLoading}
          error={wsKeysError}
          rows={workspaceKeys}
          recentlySavedId={recentlySavedId}
          onDelete={setConfirmDelete}
          onInvalidate={invalidateBoth}
          emptyCta={{
            label: "+ Add workspace key",
            onClick: () => openCreate("workspace"),
          }}
        />

        <ApiKeysSection
          title="Tenant-level keys"
          subtitle="Default keys for every workspace. Used when a workspace-level key for the same provider is not set."
          loading={tenantLoading}
          error={tenantError}
          rows={tenantKeys}
          recentlySavedId={recentlySavedId}
          onDelete={setConfirmDelete}
          onInvalidate={invalidateBoth}
          emptyCta={{
            label: "+ Add tenant key",
            onClick: () => openCreate("tenant"),
          }}
        />

        {workspaceKeys.length === 0 &&
          tenantKeys.length === 0 &&
          !wsKeysLoading &&
          !tenantLoading && (
            <div className="settings-empty" style={{ marginTop: 16 }}>
              <p>
                No API keys configured. Pipelines will fail to start until you
                add at least one provider key at tenant or workspace scope.
              </p>
            </div>
          )}
      </div>

      {showCreate && (
        <CreateKeyModal
          initialScope={createScope}
          agencyclientId={activeWsId}
          onClose={() => setShowCreate(false)}
          onCreated={(row) => {
            setRecentlySavedId(row.id);
            invalidateBoth();
            setShowCreate(false);
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          row={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onDeleted={() => {
            invalidateBoth();
            setConfirmDelete(null);
          }}
        />
      )}
    </main>
  );
}

// ---------- Section ----------

interface SectionProps {
  title: string;
  subtitle: string;
  loading: boolean;
  error: ApiError | null;
  rows: ApiKeyRow[];
  recentlySavedId: string | null;
  onDelete: (row: ApiKeyRow) => void;
  onInvalidate: () => void;
  emptyCta: { label: string; onClick: () => void };
}

function ApiKeysSection({
  title,
  subtitle,
  loading,
  error,
  rows,
  recentlySavedId,
  onDelete,
  onInvalidate,
  emptyCta,
}: SectionProps) {
  return (
    <section style={{ marginTop: 24 }}>
      <div className="settings-subheader">
        <h2>{title}</h2>
        <p className="settings-sub">{subtitle}</p>
      </div>

      {loading && <div className="state-loading">Loading keys…</div>}

      {error && !loading && (
        <div className="state-error">
          {error instanceof ApiError && error.status === 403
            ? "You don't have permission to view API keys."
            : (error.message ?? "Could not load API keys.")}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="settings-empty">
          <p>No keys at this scope.</p>
          <button
            type="button"
            className="btn-primary"
            onClick={emptyCta.onClick}
          >
            {emptyCta.label}
          </button>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <ApiKeysTable
          rows={rows}
          recentlySavedId={recentlySavedId}
          onDelete={onDelete}
          onTested={onInvalidate}
        />
      )}
    </section>
  );
}

// ---------- Table ----------

interface TableProps {
  rows: ApiKeyRow[];
  recentlySavedId: string | null;
  onDelete: (row: ApiKeyRow) => void;
  onTested: () => void;
}

function ApiKeysTable({
  rows,
  recentlySavedId,
  onDelete,
  onTested,
}: TableProps) {
  return (
    <table className="workspaces-table">
      <thead>
        <tr>
          <th>Provider</th>
          <th>Key</th>
          <th>Status</th>
          <th>Last tested</th>
          <th>Created</th>
          <th className="ws-actions-col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <KeyRow
            key={row.id}
            row={row}
            justCreated={recentlySavedId === row.id}
            onDelete={onDelete}
            onTested={onTested}
          />
        ))}
      </tbody>
    </table>
  );
}

interface KeyRowProps {
  row: ApiKeyRow;
  justCreated: boolean;
  onDelete: (row: ApiKeyRow) => void;
  onTested: () => void;
}

interface TestResponse {
  id: string;
  provider: string;
  ok: boolean;
  status: string;
  last_tested_at: string;
  last_tested_status: string;
}

function KeyRow({ row, justCreated, onDelete, onTested }: KeyRowProps) {
  const api = useApi();
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  // FEAT-071 / S-339 — webhook URL display + secret entry, zernio (or any
  // future WEBHOOK_PROVIDERS member) only. The URL itself isn't a secret
  // (see webhook_token's own comment) — the secret is what Zernio's own
  // dashboard generates once this URL is pasted into it, entered here.
  const needsWebhook = WEBHOOK_PROVIDERS.has(row.provider as Provider);
  const [webhookSecret, setWebhookSecret] = useState("");
  const [savingSecret, setSavingSecret] = useState(false);
  const [secretError, setSecretError] = useState<string | null>(null);
  const [secretSaved, setSecretSaved] = useState(false);
  const webhookUrl = row.webhook_token ? `${API_BASE_URL}/webhooks/zernio/${row.webhook_token}` : null;

  async function handleSaveWebhookSecret() {
    setSavingSecret(true);
    setSecretError(null);
    setSecretSaved(false);
    try {
      await api(`/api-keys/${row.id}`, {
        method: "PATCH",
        body: JSON.stringify({ webhook_secret: webhookSecret }),
      });
      setSecretSaved(true);
      setWebhookSecret("");
    } catch (err) {
      setSecretError(err instanceof ApiError ? err.message : "Could not save the webhook secret.");
    } finally {
      setSavingSecret(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestError(null);
    try {
      const res = await api<TestResponse>(`/api-keys/${row.id}/test`, {
        method: "POST",
      });
      // Surface failure inline; success state comes through the
      // invalidation/refetch path so the row reflects the server row.
      if (!res.ok) {
        setTestError(res.last_tested_status || "Test failed.");
      }
      onTested();
    } catch (err) {
      if (err instanceof ApiError) {
        setTestError(err.message);
      } else {
        setTestError("Could not test key.");
      }
    } finally {
      setTesting(false);
    }
  }

  const statusPill = (() => {
    if (row.status === "active") return <span className="pill pill-ok">Active</span>;
    if (row.status === "error") return <span className="pill pill-warn">Error</span>;
    if (row.status === "rotating") return <span className="pill pill-info">Rotating</span>;
    return <span className="pill pill-muted">{row.status}</span>;
  })();

  const lastTestedPill = (() => {
    if (!row.last_tested_at) {
      return <span className="ws-sub-meta">Never tested</span>;
    }
    const ok = row.last_tested_status === "success";
    return (
      <span className="api-keys-last-tested">
        <span
          className={`pill ${ok ? "pill-ok" : "pill-warn"}`}
          title={ok ? "Success" : (row.last_tested_status ?? "Failure")}
        >
          {ok ? "✓" : "✗"}
        </span>
        <span className="ws-date">{fmtRelative(row.last_tested_at)}</span>
      </span>
    );
  })();

  return (
    <>
      <tr className="workspaces-row">
        <td>
          <span className="ws-name">{providerLabel(row.provider)}</span>
        </td>
        <td>
          <code className="mono ws-slug api-keys-mask">
            {maskedEcho(row.provider, row.key_last4)}
          </code>
          {row.base_url && (
            <div className="mono" style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }} title="This key's own API address">
              {row.base_url}
            </div>
          )}
          {justCreated && (
            <span
              className="editor-saved-badge"
              role="status"
              style={{ marginLeft: 8 }}
            >
              Saved
            </span>
          )}
        </td>
        <td>{statusPill}</td>
        <td>
          {lastTestedPill}
          {testError && (
            <span
              className="settings-field-error"
              title={testError}
              style={{ display: "block", marginTop: 4 }}
            >
              {testError.length > 60 ? `${testError.slice(0, 60)}…` : testError}
            </span>
          )}
        </td>
        <td className="ws-date">{fmtDate(row.created_at)}</td>
        <td className="ws-actions">
          <button
            type="button"
            className="btn-ghost"
            onClick={handleTest}
            disabled={testing}
          >
            {testing ? "Testing…" : "Test"}
          </button>
          <button
            type="button"
            className="btn-ghost btn-danger"
            onClick={() => onDelete(row)}
          >
            Delete
          </button>
        </td>
      </tr>
      {needsWebhook && webhookUrl && (
        <tr className="workspaces-row">
          <td colSpan={6}>
            <div className="ws-sub-meta" style={{ marginBottom: 4 }}>
              Webhook URL — paste this into your Zernio dashboard's webhook settings, then
              paste the secret it gives you back below:
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="text"
                className="settings-input mono"
                readOnly
                value={webhookUrl}
                onFocus={(e) => e.currentTarget.select()}
                style={{ flex: "1 1 320px", fontSize: 12 }}
              />
              <button
                type="button"
                className="btn-ghost"
                onClick={() => navigator.clipboard.writeText(webhookUrl)}
              >
                Copy
              </button>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
              <input
                type="password"
                className="settings-input"
                placeholder="Webhook secret from Zernio"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                style={{ flex: "1 1 320px" }}
              />
              <button
                type="button"
                className="btn-ghost"
                disabled={savingSecret || !webhookSecret.trim()}
                onClick={handleSaveWebhookSecret}
              >
                {savingSecret ? "Saving…" : "Save secret"}
              </button>
              {secretSaved && (
                <span className="editor-saved-badge" role="status">
                  Saved
                </span>
              )}
            </div>
            {secretError && (
              <span className="settings-field-error" style={{ display: "block", marginTop: 4 }}>
                {secretError}
              </span>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ---------- Create modal ----------

interface CreateProps {
  initialScope: Scope;
  agencyclientId: number;
  onClose: () => void;
  onCreated: (row: ApiKeyRow) => void;
}

function CreateKeyModal({
  initialScope,
  agencyclientId,
  onClose,
  onCreated,
}: CreateProps) {
  const api = useApi();
  const [scope, setScope] = useState<Scope>(initialScope);
  const [provider, setProvider] = useState<Provider>("anthropic");
  // SECURITY: the key value lives only inside this component's local
  // state and is cleared by React on unmount. We never log it, copy
  // it elsewhere, or persist it. The form is uncontrolled-style for
  // password best practice but kept controlled for canSubmit gating.
  const [keyValue, setKeyValue] = useState("");
  // BUG-124 — the API address Alibaba gives a Qwen workspace; only asked for
  // providers that take one.
  const [baseUrl, setBaseUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<ApiError | null>(null);

  const keyError =
    keyValue.length > 0 &&
    (keyValue.length < 20 || keyValue.length > 500)
      ? "Provider key must be 20–500 characters."
      : null;

  const canSubmit =
    !submitting && keyValue.trim().length >= 20 && !keyError;

  const collisionError =
    serverError instanceof ApiError && serverError.status === 409;
  const validationError =
    serverError instanceof ApiError && serverError.status === 422;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const body = buildCreateKeyBody({
        scope,
        agencyclientId,
        provider,
        key: keyValue,
        baseUrl,
      });
      const created = await api<ApiKeyRow>("/api-keys", {
        method: "POST",
        body: JSON.stringify(body),
      });
      // SECURITY: drop the plaintext from local state immediately on
      // success so a later DevTools state inspection won't surface it.
      setKeyValue("");
      onCreated(created);
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err);
      } else {
        setServerError(new ApiError(0, String(err), "Could not save key"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell onClose={onClose} eyebrow="API keys" title="New API key">
      <form className="settings-form" onSubmit={submit}>
        <label className="settings-field">
          <span className="settings-label">Scope</span>
          <select
            className="settings-select"
            value={scope}
            onChange={(e) => setScope(e.target.value as Scope)}
          >
            <option value="workspace">
              {SCOPE_LABEL.workspace} — only this workspace
            </option>
            <option value="tenant">
              {SCOPE_LABEL.tenant} — every workspace (fallback)
            </option>
          </select>
          <span className="settings-label-hint">
            Workspace-scoped keys override tenant-scoped keys for the same
            provider when a pipeline runs under that workspace.
          </span>
        </label>

        <label className="settings-field">
          <span className="settings-label">Provider</span>
          <select
            className="settings-select"
            value={provider}
            onChange={(e) => setProvider(e.target.value as Provider)}
          >
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {PROVIDER_LABEL[p]}
              </option>
            ))}
          </select>
        </label>

        <label className="settings-field">
          <span className="settings-label">
            Key{" "}
            <span className="settings-label-hint">
              Encrypted server-side with AES-256-GCM. Never echoed back as
              plaintext after save.
            </span>
          </span>
          <input
            className="settings-input mono"
            type="password"
            value={keyValue}
            onChange={(e) => setKeyValue(e.target.value)}
            autoFocus
            autoComplete="new-password"
            spellCheck={false}
            data-lpignore="true"
            data-1p-ignore="true"
            placeholder={`Paste your ${PROVIDER_LABEL[provider]} API key`}
            maxLength={500}
          />
          {keyError && (
            <span className="settings-field-error">{keyError}</span>
          )}
        </label>

        {providerTakesBaseUrl(provider) && (
          <label className="settings-field">
            <span className="settings-label">
              API base URL{" "}
              <span className="settings-label-hint">
                Optional. Alibaba gives each workspace its own address; a key only
                works at its own. Copy it from the Model Studio console, e.g.
                https://ws-xxxx.ap-southeast-1.maas.aliyuncs.com/compatible-mode/v1.
                Leave blank for the China endpoint.
              </span>
            </span>
            <input
              className="settings-input mono"
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              placeholder="https://…aliyuncs.com/compatible-mode/v1"
              maxLength={200}
            />
          </label>
        )}

        {collisionError && (
          <div className="settings-form-error">
            An active {PROVIDER_LABEL[provider]} key already exists at this
            scope. Delete the existing one before adding a replacement.
          </div>
        )}
        {validationError && !collisionError && (
          <div className="settings-form-error">
            {serverError?.body || "The server rejected the request."}
          </div>
        )}
        {serverError &&
          !collisionError &&
          !validationError &&
          serverError.status !== 409 && (
            <div className="settings-form-error">{serverError.message}</div>
          )}

        <div className="settings-form-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
          >
            {submitting ? "Saving…" : "Save key"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---------- Delete confirm ----------

interface ConfirmDeleteProps {
  row: ApiKeyRow;
  onCancel: () => void;
  onDeleted: () => void;
}

function ConfirmDeleteModal({
  row,
  onCancel,
  onDeleted,
}: ConfirmDeleteProps) {
  const mutation = useApiMutation<void, void>(
    `/api-keys/${row.id}`,
    "DELETE",
    {
      onSuccess: () => onDeleted(),
    }
  );

  return (
    <ModalShell onClose={onCancel} eyebrow="API keys · danger" title="Delete API key?">
      <p className="settings-confirm-copy">
        Remove this <strong>{providerLabel(row.provider)}</strong> key (
        <code className="mono">{maskedEcho(row.provider, row.key_last4)}</code>
        )? Pipeline runs will fall back to the next level in the resolution
        hierarchy
        {row.agencyclient_id == null
          ? " (CCM platform default, if configured)."
          : " (the tenant-level key for this provider, or the platform default)."}
      </p>
      {mutation.error && (
        <div className="settings-form-error">{mutation.error.message}</div>
      )}
      <div className="settings-form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary btn-danger"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Deleting…" : "Delete key"}
        </button>
      </div>
    </ModalShell>
  );
}

// NOTE: the local ModalShell / DrawerShell that used to live here were removed
// in the option-3 consolidation — both now come from
// @/components/SettingsModalShell (imported at the top of this file). The shared
// shell renders the canonical anatomy (eyebrow + 24px serif title, bordered
// close, blurred overlay) and handles Escape/backdrop close.
